import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { Check, ArrowRight, Satellite } from "lucide-react";

const IMG =
  "https://static.prod-images.emergentagent.com/jobs/5c31e2fe-4abc-4e4f-a3d7-a717d07955ff/images/cdab8a501a0a39d796ab9d632cf8871452080a883a593d3a13e466e157c1d575.png";

const StarlinkSection = () => {
  const { t } = useLanguage();
  const benefits = ["b1", "b2", "b3", "b4", "b5"].map((k) => t(`starlink.${k}`));
  const steps = ["step1", "step2", "step3", "step4"].map((k) => t(`starlink.${k}`));

  return (
    <section
      id="starlink"
      data-testid="starlink-section"
      className="relative py-24 lg:py-32 bg-neutral-950 text-white overflow-hidden"
    >
      <div className="absolute inset-0 opacity-30">
        <div className="absolute -top-40 -left-20 w-[480px] h-[480px] rounded-full bg-sky-600/40 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-amber-500/15 blur-3xl" />
      </div>

      <div className="container-px mx-auto max-w-7xl relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs uppercase tracking-[0.22em] text-sky-300">
              <Satellite className="w-3.5 h-3.5" />
              {t("starlink.eyebrow")}
            </div>
            <h2 className="font-display mt-5 text-4xl lg:text-6xl font-light tracking-tighter text-balance">
              {t("starlink.title")}
            </h2>
            <p className="mt-6 text-neutral-300 leading-relaxed max-w-xl">
              {t("starlink.p1")}
            </p>
            <p className="mt-4 text-neutral-300 leading-relaxed max-w-xl">
              {t("starlink.p2")}
            </p>

            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {benefits.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-[15px] text-neutral-200"
                  data-testid={`starlink-benefit-${i}`}
                >
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-sky-500/20 grid place-items-center">
                    <Check className="w-3 h-3 text-sky-300" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>

            <a
              href="#contact"
              data-testid="starlink-cta"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-white text-neutral-950 px-6 py-4 text-sm font-medium hover:bg-sky-50 transition-colors"
            >
              {t("starlink.cta")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
              <img src={IMG} alt="Starlink Installation Mallorca" className="w-full h-auto" />
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {steps.map((s, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-white/5 border border-white/10 p-4 overflow-hidden"
                  data-testid={`starlink-step-${i}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] text-sky-300">
                    0{i + 1}
                  </div>
                  <div className="mt-1 text-xs font-medium leading-snug break-words">
                    {s}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StarlinkSection;
